import { useContext, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import WorkOrdersContext from '@atom/components/workOrders/WorkOrdersContext';
import textConstants from '@atom/constants/textConstants';
import {
  WORK_FILTER_CREATE,
  WORK_FILTER_DELETE,
  WORK_FILTER_UPDATE,
} from '@atom/graph/workFilters';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import {
  FE_PARAMS,
  INITIAL_INPUT,
  WorkOrdersInputState,
} from '@atom/hooks/useWorkOrdersFilters';
import { Icon, IconButton, Modal, Popover, Snackbar } from '@atom/mui';
import { KeyboardArrowDownIcon } from '@atom/mui/icons';
import {
  presetDateFilterKeys,
  WorkDatePresetKey,
  WorkFilterSource,
} from '@atom/types/workFilters';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  getDateKeyFromPreset,
  isCustomDatePreset,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import classes from './workOrdersFilters.module.css';

const SYSTEM_DEFAULT_FILTER_ID = 'webDefault';

const WorkFiltersControls = () => {
  const anchor = useRef<HTMLDivElement>(null);
  const userProfile = useUserProfile();
  const { workOrdersInputCart, getWorkFilters, input, setInput } =
    useContext(WorkOrdersContext);

  const getWorkFiltersParams = {
    variables: {
      input: {
        userId: userProfile.userId,
        source: WorkFilterSource.WEB,
      },
    },
  };

  const { dateFilterPresetsCart } = workOrdersInputCart;

  const defaultFilter = input.defaultFilter;

  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [saveOpen, setSaveOpen] = useState<boolean>(false);
  const [restoreOpen, setRestoreOpen] = useState<boolean>(false);

  const [workFilterCreate, { loading: loadingCreate }] =
    useMutation(WORK_FILTER_CREATE);

  const [workFilterUpdate, { loading: loadingUpdate }] =
    useMutation(WORK_FILTER_UPDATE);

  const [workFilterDelete, { loading: loadingDelete }] =
    useMutation(WORK_FILTER_DELETE);

  const isSystemDefaultFilter = defaultFilter?.id === SYSTEM_DEFAULT_FILTER_ID;

  const resetState = () => {
    setPopoverOpen(false);
    setSaveOpen(false);
    setRestoreOpen(false);
  };

  const removePresetDates = (cartPortion: {}, type: WorkDatePresetKey) => {
    return presetDateFilterKeys.reduce((prev, key) => {
      if (
        R.isNil(dateFilterPresetsCart[type][key]) ||
        isCustomDatePreset(dateFilterPresetsCart[type][key])
      ) {
        return prev;
      }
      return {
        ...prev,
        [getDateKeyFromPreset(key, 'Start')]: null,
        [getDateKeyFromPreset(key, 'End')]: null,
      };
    }, cartPortion);
  };

  const sanitizeDateParams = () => ({
    ...removePresetDates(workOrdersInputCart, WorkDatePresetKey.WORK),
    tasks: removePresetDates(workOrdersInputCart.tasks, WorkDatePresetKey.TASK),
  });

  const cleanDatePresets = presets => ({
    ...R.reject(isNilOrEmpty, {
      [WorkDatePresetKey.WORK]: {
        ...R.reject(isNilOrEmpty, presets[WorkDatePresetKey.WORK]),
      },
      [WorkDatePresetKey.TASK]: {
        ...R.reject(isNilOrEmpty, presets[WorkDatePresetKey.TASK]),
      },
    }),
  });

  const createWorkFilter = async () => {
    resetState();

    try {
      Snackbar.info({
        message: 'Saving filter ...',
      });

      await workFilterCreate({
        variables: {
          input: {
            userId: userProfile?.userId,
            source: WorkFilterSource.WEB,
            name: 'Custom',
            isDefault: true,
            params: R.omit(
              FE_PARAMS,
              R.reject(
                isNilOrEmpty,
                sanitizeDateParams(),
              ) as WorkOrdersInputState,
            ),
            ...cleanDatePresets(dateFilterPresetsCart),
          },
        },
      });

      await getWorkFilters(getWorkFiltersParams);

      Snackbar.info({
        message: 'Current filter saved as default.',
      });
    } catch (err) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const updateWorkFilter = async () => {
    resetState();

    try {
      Snackbar.info({
        message: 'Saving filter ...',
      });

      await workFilterUpdate({
        variables: {
          input: {
            workFilterId: defaultFilter?.id,
            params: R.omit(
              FE_PARAMS,
              R.reject(
                isNilOrEmpty,
                sanitizeDateParams(),
              ) as WorkOrdersInputState,
            ),
            ...cleanDatePresets(dateFilterPresetsCart),
          },
        },
      });

      await getWorkFilters(getWorkFiltersParams);

      Snackbar.info({
        message: 'Current filter saved as default.',
      });
    } catch (err) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const deleteWorkFilter = async () => {
    resetState();

    try {
      Snackbar.info({
        message: 'Restoring filter ...',
      });

      await workFilterDelete({
        variables: {
          id: defaultFilter?.id,
        },
      });

      setInput(INITIAL_INPUT);

      await getWorkFilters(getWorkFiltersParams);

      Snackbar.info({
        message: 'Restored default filter.',
      });
    } catch (err) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const loading = loadingCreate || loadingDelete || loadingUpdate;

  return (
    <>
      <div ref={anchor} className={classes.filterHeader}>
        Filter
        <IconButton
          onClick={() => setPopoverOpen(true)}
          tooltip="Filter options"
        >
          <Icon icon={KeyboardArrowDownIcon} />
        </IconButton>
      </div>
      <Popover
        open={popoverOpen}
        anchorEl={anchor.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setPopoverOpen(false)}
      >
        <div className={classes.filterPopover}>
          <div className={classes.filterPopoverHeader}>
            <div>Default Filter:</div>
            <div className={classes.filterValue}>
              {isSystemDefaultFilter ? 'System Default' : defaultFilter?.name}
            </div>
          </div>
          <ul className={classes.defaultFilterMenu}>
            <li
              className={classes.defaultFilterMenuItem}
              onClick={() => setSaveOpen(true)}
            >
              Save Current Filter as Default
            </li>
            {!isSystemDefaultFilter && (
              <li
                className={classes.defaultFilterMenuItem}
                onClick={() => setRestoreOpen(true)}
              >
                Restore Default Filter
              </li>
            )}
          </ul>
        </div>
      </Popover>
      <Modal
        open={saveOpen}
        loading={loading}
        cancelButtonText="Cancel"
        confirmButtonText="Save & Apply"
        onCancel={resetState}
        onConfirm={isSystemDefaultFilter ? createWorkFilter : updateWorkFilter}
        title="Save Current Filter as Default?"
      >
        Are you sure you want to save the current filter as the default?
      </Modal>
      <Modal
        open={restoreOpen}
        loading={loading}
        cancelButtonText="Cancel"
        confirmButtonText="Restore & Apply"
        onCancel={resetState}
        onConfirm={deleteWorkFilter}
        title="Restore Default Filter?"
      >
        Are you sure you want to restore the default filter to system default?
        This will remove all of your custom changes to the default filter.
      </Modal>
    </>
  );
};

export default WorkFiltersControls;
